import { useEffect, useRef, useState, type ReactNode } from "react";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

const SECOND = 1_000;
const HOUR_S = 3_600;
const DAY_S = 24 * HOUR_S;

interface CountdownProps {
  opensOn: string;
  closesOn: string;
  /** The server's clock when the window was read, so a wrong device clock does not matter. */
  serverNow: string;
  /** The device time the window was read at, i.e. react-query's `dataUpdatedAt`. */
  receivedAt: number;
  /** Called when the window opens or closes while this is on screen. */
  onPhaseChange?: () => void;
}

type Phase = "opent" | "sluit" | "gesloten";

const two = (n: number) => String(n).padStart(2, "0");

/**
 * Ticks every second on screen, but screen readers only get days, then hours: a live region that
 * changed every second would be read out every second.
 */
export function Countdown({
  opensOn,
  closesOn,
  serverNow,
  receivedAt,
  onPhaseChange,
}: CountdownProps) {
  const { t } = useTranslation();
  const now = useServerNow(serverNow, receivedAt);
  const opens = Date.parse(opensOn);
  const closes = Date.parse(closesOn);
  const phase: Phase =
    now < opens ? "opent" : now < closes ? "sluit" : "gesloten";

  useOnChange(phase, onPhaseChange);

  if (phase === "gesloten") {
    return (
      <Banner>
        <p role="status" className="text-lg font-semibold">
          {t("countdown.gesloten")}
        </p>
      </Banner>
    );
  }

  const target = phase === "opent" ? opens : closes;

  return (
    <Running
      phase={phase}
      seconds={Math.max(0, Math.ceil((target - now) / SECOND))}
    />
  );
}

/** The server's time now: what it said when the data arrived, plus the device time since. */
function useServerNow(serverNow: string, receivedAt: number) {
  const [deviceNow, setDeviceNow] = useState(Date.now);

  useEffect(() => {
    const timer = setInterval(() => setDeviceNow(Date.now()), SECOND);
    return () => clearInterval(timer);
  }, []);

  return Date.parse(serverNow) + (deviceNow - receivedAt);
}

// The host refetches at each crossing: the server's isOpen, and so what the screens unlock, moves with it.
function useOnChange(phase: Phase, onChange: (() => void) | undefined) {
  const last = useRef(phase);

  useEffect(() => {
    if (last.current === phase) return;
    last.current = phase;
    onChange?.();
  }, [phase, onChange]);
}

function Running({
  phase,
  seconds,
}: {
  phase: "opent" | "sluit";
  seconds: number;
}) {
  const { t } = useTranslation();
  const days = Math.floor(seconds / DAY_S);
  const hours = Math.floor((seconds % DAY_S) / HOUR_S);
  const tijd = `${two(hours)}:${two(Math.floor((seconds % HOUR_S) / 60))}:${two(seconds % 60)}`;
  const grof = useGrof(days, hours);
  const opent = phase === "opent";

  return (
    <Banner>
      <div aria-hidden>
        <p className="text-sm font-medium">
          {t(opent ? "countdown.opent" : "countdown.sluit")}
        </p>
        <p className="text-2xl font-bold tabular-nums">
          {days > 0 ? t("countdown.dagEnTijd", { count: days, tijd }) : tijd}
        </p>
      </div>
      <p role="status" className="sr-only">
        {t(opent ? "countdown.opentOver" : "countdown.sluitOver", {
          resterend: grof,
        })}
      </p>
    </Banner>
  );
}

/** What a screen reader hears of the time left: days, else hours, else "less than an hour". */
function useGrof(days: number, hours: number) {
  const { t } = useTranslation();

  if (days > 0) return t("countdown.dag", { count: days });
  if (hours > 0) return t("countdown.uren", { aantal: hours });
  return t("countdown.minderDanEenUur");
}

function Banner({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-info-border bg-info-surface p-4">
      <Clock aria-hidden className="size-6 shrink-0 text-info-foreground" />
      <div>{children}</div>
    </div>
  );
}
